import * as Sentry from "@sentry/react";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BlockRenderer } from "../components/education/article/BlockRenderer";
import { EdHubDetailShell } from "../components/education/EdHubDetailShell";
import { useResolvedImageUrl } from "../hooks/useResolvedImageUrl";
import { useAnalytics } from "../lib/analytics";
import { formatToSegment } from "../lib/edhub/formatToSegment";
import { useClientTranslations } from "../lib/i18n/client";
import {
	type Article,
	type EducationHubArticleDetail,
	fetchArticle,
	fetchArticleDetail,
} from "../services/educationService";
import styles from "./EducationArticle.module.css";

/**
 * Full-screen article reader. Figma node 2420:25594.
 * Fetches the article detail from era-api `/api/education-hub/articles/:id`,
 * then loads the structured Article JSON from `articleUrl` and renders it
 * through BlockRenderer.
 *
 * The structured article lands once the prism worker finishes; until then
 * `articleUrl` is null (or the artefact 404s) and we show a pending state.
 */
export function EducationArticle() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { logEducation } = useAnalytics();
	const { t } = useClientTranslations("education");
	const [detail, setDetail] = useState<EducationHubArticleDetail | null>(null);
	const [article, setArticle] = useState<Article | null>(null);
	const [error, setError] = useState<string | null>(null);

	const hasTrackedPageView = useRef(false);
	useEffect(() => {
		if (hasTrackedPageView.current) return;
		hasTrackedPageView.current = true;
		logEducation("article_page_viewed", { contentId: id });
	}, [logEducation, id]);
	// Resolve the auth-gated proxy hero to a bearer-fetched blob (presigned URLs
	// pass through); loading/error/idle → placeholder shows; loaded → hero.
	const { url: heroUrl } = useResolvedImageUrl(detail?.imageUrl);

	useEffect(() => {
		if (!id) return;
		let cancelled = false;
		fetchArticleDetail(id)
			.then(async (d) => {
				if (cancelled) return;
				setDetail(d);
				if (d.articleUrl) {
					try {
						const a = await fetchArticle(d.articleUrl);
						if (!cancelled) setArticle(a);
					} catch (err) {
						// Article still rendering, unparseable, or fetch failed — keep
						// the pending state, but surface the error so a device-only
						// failure (e.g. cross-origin block) is diagnosable.
						console.error("education article fetch failed", err);
						Sentry.captureException(err);
					}
				}
			})
			.catch((e) => {
				if (!cancelled) setError(String(e?.message ?? e));
			});
		return () => {
			cancelled = true;
		};
	}, [id]);

	return (
		<EdHubDetailShell
			availableFormats={["text", "cards", "audio"]}
			onFormatSelect={(format) =>
				id && navigate(`/education/${id}/${formatToSegment(format)}`)
			}
			onClose={() => navigate("/education")}
			closeLabel={t("article.close", "Close")}
		>
			<div className={styles.content}>
				{error && <p className={styles.error}>{error}</p>}
				{!detail && !error && (
					<p className={styles.loading}>{t("article.loading", "Loading…")}</p>
				)}
				{detail &&
					(article ? (
						<>
							<h1 className={styles.title}>{article.title}</h1>
							<p className={styles.meta}>
								{t("article.readTime", {
									minutes: article.read_time_minutes,
									defaultValue: "{{minutes}} min read",
								})}
								{article.author?.name
									? ` • ${t("article.writtenBy", {
											author: article.author.title
												? `${article.author.name}, ${article.author.title}`
												: article.author.name,
											defaultValue: "Written by {{author}}",
										})}`
									: ""}
							</p>
							{heroUrl ? (
								// Render the hero at its natural aspect ratio (full width, auto
								// height) so nothing is cropped and there are no side gaps; the
								// card height just follows the image (86ey1dpdx).
								<img
									className={styles.hero}
									src={heroUrl}
									alt=""
									aria-hidden="true"
								/>
							) : (
								<div className={styles.heroPlaceholder} aria-hidden="true" />
							)}
							<article className={styles.body}>
								<div className={styles.calloutBox}>{article.intro}</div>
								<BlockRenderer blocks={article.blocks} />
								{article.disclaimer && (
									<p className={styles.disclaimer}>{article.disclaimer}</p>
								)}
							</article>
						</>
					) : (
						<>
							<h1 className={styles.title}>{detail.title}</h1>
							<p className={styles.meta}>
								{detail.publishedAt
									? new Date(detail.publishedAt).toLocaleDateString()
									: ""}
							</p>
							<div className={styles.pending}>
								<p>
									{t(
										"article.pending",
										"Article is still being prepared by the AI pipeline.",
									)}
								</p>
								<p className={styles.pendingHint}>
									{t(
										"article.pendingHint",
										"Check back in a few minutes — we'll have your read ready.",
									)}
								</p>
							</div>
						</>
					))}
			</div>
		</EdHubDetailShell>
	);
}
